
import { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { TradingAccountOption } from '@/types/trader';
import { generateSecureState, checkRateLimit, clearRateLimit, securityCleanup } from '@/utils/oauth-security';
import { categorizeOAuthError, getRetryDelay, circuitBreaker } from '@/utils/oauth-errors';
import { memoryOAuthCache } from '@/utils/oauth-cache-memory';
import { apiConnectionService } from '@/services/api-connection-service';
import { securityMonitor, logOAuthFailure, logRateLimitHit, logCSRFMismatch } from '@/utils/security-monitor';
import { 
  oauthAnalytics, 
  startOAuthTracking, 
  trackOAuthStart,
  trackOAuthAuthentication,
  trackAccountsFetch,
  trackOAuthCompletion,
  trackOAuthRetry,
  trackCacheHit 
} from '@/utils/oauth-analytics';
import { logger, logOAuthFlow, logPerformance, logError, logWarning } from '@/utils/production-logger';

interface OAuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface CTraderAccount {
  accountId: string;
  accountNumber: string;
  live: boolean;
  balance: number;
  depositCurrency: string;
  moneyDigits: number;
  brokerName?: string;
  brokerTitle?: string;
}

const OAUTH_TIMEOUT = 60000; // Extended to 60 seconds (was 45)
const MAX_RETRY_ATTEMPTS = 3;

export function useSimpleCTraderOAuth() {
  const { toast } = useToast();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokens, setTokens] = useState<OAuthTokens | null>(null);
  const [tradingAccounts, setTradingAccounts] = useState<TradingAccountOption[]>([]);
  const [rawAccounts, setRawAccounts] = useState<CTraderAccount[]>([]);
  const [currentError, setCurrentError] = useState<any>(null);
  const [retryCount, setRetryCount] = useState(0);
  
  const fetchAttemptedRef = useRef(false);
  const isInitializedRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const currentSessionId = useRef<string | null>(null);

  // Run security cleanup and cache maintenance on mount
  useEffect(() => {
    securityCleanup();
    memoryOAuthCache.performMaintenance();
  }, []);

  // Debounced function to prevent multiple OAuth requests
  const debouncedStartOAuth = useCallback(
    debounce(async (cspAccountId: string) => {
      return await startOAuthInternal(cspAccountId);
    }, 500),
    []
  );

  // Function to get admin credentials with enhanced caching and network resilience
  const getAdminCredentials = async () => {
    const startTime = Date.now();
    
    // Try memory cache first
    const cached = memoryOAuthCache.getAdminCredentials();
    if (cached) {
      trackCacheHit(true);
      logPerformance('getAdminCredentials_cache', startTime);
      return cached;
    }

    trackCacheHit(false);

    try {
      const credentials = await apiConnectionService.getAdminCredentials();
      
      // Cache the credentials with encryption and memory storage
      memoryOAuthCache.setAdminCredentials(credentials);
      
      logPerformance('getAdminCredentials_fetch', startTime);
      return credentials;
    } catch (error) {
      logError('Error fetching admin credentials', error as Error);
      throw error;
    }
  };
  
  // Enhanced reset state with security cleanup and cache management
  const resetState = useCallback(() => {
    // Clear timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = undefined;
    }
    
    setTokens(null);
    setTradingAccounts([]);
    setRawAccounts([]);
    setIsAuthenticating(false);
    setIsLoading(false);
    setCurrentError(null);
    setRetryCount(0);
    fetchAttemptedRef.current = false;
    currentSessionId.current = null;
    
    // Clear OAuth-specific localStorage keys
    const keysToRemove = [
      'ctrader_oauth_result',
      'ctrader_form_data',
      'ctrader_connection_id'
    ];
    
    keysToRemove.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear cache and perform security cleanup with memory cache
    memoryOAuthCache.clearAll();
    securityCleanup();
    
    // Ensure return path is properly set
    const currentPath = window.location.pathname;
    localStorage.setItem('ctrader_return_path', currentPath);
    
    logger.debug('OAuth state reset completed');
  }, []);

  // Check for OAuth results with enhanced security validation
  useEffect(() => {
    if (isInitializedRef.current) return;
    
    const checkForOAuthResults = async () => {
      try {
        logger.debug('Checking for OAuth results with enhanced validation');
        const savedResult = localStorage.getItem('ctrader_oauth_result');
        if (savedResult) {
          logger.info('Found OAuth result in localStorage');
          const parsedResult = JSON.parse(savedResult);
          
          if (parsedResult.type === 'ctrader-oauth-tokens' && parsedResult.tokens) {
            logger.info('Processing OAuth tokens from localStorage');
            await handleTokensReceived(parsedResult.tokens);
            localStorage.removeItem('ctrader_oauth_result');
            logger.debug('Cleaned up ctrader_oauth_result from localStorage');
          }
          
          if (parsedResult.type === 'ctrader-oauth-error') {
            handleAuthError(parsedResult.error || 'Unknown error occurred');
            localStorage.removeItem('ctrader_oauth_result');
          }
        }
      } catch (e) {
        logError('Error checking for OAuth results', e as Error);
        const error = categorizeOAuthError(e);
        setCurrentError(error);
      }
    };
    
    checkForOAuthResults();
    isInitializedRef.current = true;
  }, []);

  // Handle tokens received from OAuth
  const handleTokensReceived = async (receivedTokens: any) => {
    const startTime = Date.now();
    logger.debug('Processing OAuth tokens with enhanced performance');
    
    // Clear timeout since we received tokens
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    
    const normalizedTokens = {
      accessToken: receivedTokens.accessToken || receivedTokens.access_token,
      refreshToken: receivedTokens.refreshToken || receivedTokens.refresh_token,
      expiresIn: receivedTokens.expiresIn || receivedTokens.expires_in,
    };
    
    if (typeof normalizedTokens.expiresIn === 'string') {
      normalizedTokens.expiresIn = parseInt(normalizedTokens.expiresIn, 10) || 0;
    }
    
    logger.debug('Setting tokens state');
    setTokens(normalizedTokens);
    setCurrentError(null);
    setRetryCount(0);
    
    // Record success for circuit breaker
    circuitBreaker.recordSuccess();
    
    // Track authentication success
    if (currentSessionId.current) {
      trackOAuthAuthentication(currentSessionId.current, true);
    }
    
    // Fetch trading accounts with enhanced error handling
    logger.debug('Starting to fetch trading accounts');
    await fetchTradingAccounts(normalizedTokens.accessToken);
    setIsAuthenticating(false);
    
    logPerformance('handleTokensReceived', startTime);
    logger.debug('OAuth token processing completed');
  };

  // Enhanced error handling with circuit breaker integration
  const handleAuthError = (errorMessage: string) => {
    logError('OAuth error occurred', new Error(errorMessage));
    
    // Record failure for circuit breaker
    circuitBreaker.recordFailure();
    
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    
    const categorizedError = categorizeOAuthError({ message: errorMessage });
    setCurrentError(categorizedError);
    
    // Track authentication failure
    if (currentSessionId.current) {
      trackOAuthAuthentication(currentSessionId.current, false, errorMessage);
    }
    
    // Log security event
    logOAuthFailure('anonymous', errorMessage);
    
    toast({
      title: categorizedError.userMessage,
      description: categorizedError.actionable,
      variant: "destructive",
    });
    
    setIsAuthenticating(false);
  };

  // Enhanced startOAuth with circuit breaker and network quality check
  const startOAuthInternal = async (cspAccountId: string = '') => {
    const startTime = Date.now();
    
    if (!cspAccountId) {
      const error = categorizeOAuthError({ message: "CSP Account ID is required" });
      setCurrentError(error);
      logWarning('OAuth start failed: missing CSP Account ID');
      toast({
        title: error.userMessage,
        description: error.actionable,
        variant: "destructive",
      });
      return false;
    }

    // Check circuit breaker
    if (!circuitBreaker.canExecute()) {
      const error = categorizeOAuthError({ message: 'Circuit breaker open - service temporarily unavailable' });
      setCurrentError(error);
      logWarning('OAuth blocked by circuit breaker');
      toast({
        title: "Service Temporarily Unavailable",
        description: "Multiple failures detected. Please wait a moment before trying again.",
        variant: "destructive",
      });
      return false;
    }

    // Check rate limiting
    const rateLimitCheck = checkRateLimit(cspAccountId);
    if (!rateLimitCheck.allowed) {
      logRateLimitHit(cspAccountId, 'oauth_start');
      toast({
        title: "Rate Limited",
        description: rateLimitCheck.reason,
        variant: "destructive",
      });
      return false;
    }

    // Check network status
    const networkStatus = apiConnectionService.getNetworkStatus();
    if (networkStatus.quality === 'offline') {
      const error = categorizeOAuthError({ message: 'No internet connection' });
      setCurrentError(error);
      logWarning('OAuth failed: no internet connection');
      toast({
        title: "No Internet Connection",
        description: "Please check your connection and try again.",
        variant: "destructive",
      });
      return false;
    }
    
    logger.debug('Starting enhanced OAuth flow with performance optimization');
    
    // Start analytics tracking
    currentSessionId.current = startOAuthTracking(cspAccountId, retryCount > 0 ? 'retry' : 'new');
    trackOAuthStart(currentSessionId.current);
    
    // Reset state for new authentication flow
    resetState();
    currentSessionId.current = startOAuthTracking(cspAccountId, retryCount > 0 ? 'retry' : 'new');
    setIsAuthenticating(true);
    
    // Set extended timeout for OAuth flow (60 seconds)
    timeoutRef.current = setTimeout(() => {
      logger.warn('OAuth timeout reached (60 seconds)');
      handleAuthError('TIMEOUT: OAuth flow took too long (60s). Please try again.');
      
      // Track timeout
      if (currentSessionId.current) {
        trackOAuthAuthentication(currentSessionId.current, false, 'timeout');
      }
      
      // Cleanup localStorage on timeout
      localStorage.removeItem('ctrader_oauth_result');
      localStorage.removeItem('ctrader_form_data');
      
      setIsAuthenticating(false);
    }, OAUTH_TIMEOUT);
    
    try {
      // Get admin credentials with enhanced caching and network resilience
      const adminCreds = await getAdminCredentials();
      
      // Set return path
      const currentPath = window.location.pathname + window.location.search;
      localStorage.setItem('ctrader_return_path', currentPath);
      
      // Generate secure state with enhanced CSRF protection
      const state = generateSecureState({
        appOrigin: window.location.origin,
        returnPath: currentPath,
        cspAccountId: cspAccountId
      });
      
      // Generate the redirect URL
      const redirectUri = `${window.location.origin}/auth/callback`;
      const scope = "trading";
      
      // Construct the cTrader OAuth URL with enhanced security
      const oauthUrl = `https://id.ctrader.com/my/settings/openapi/grantingaccess?client_id=${encodeURIComponent(adminCreds.client_id)}&scope=${encodeURIComponent(scope)}&state=${state}&redirect_uri=${encodeURIComponent(redirectUri)}`;
      
      logOAuthFlow('start', true, { metadata: { cspAccountId }, sessionId: currentSessionId.current });
      logPerformance('startOAuth', startTime);
      
      window.location.href = oauthUrl;
      return true;
    } catch (error) {
      logError('Error in OAuth flow', error as Error, { metadata: { cspAccountId } });
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      
      const categorizedError = categorizeOAuthError(error);
      setCurrentError(categorizedError);
      
      // Track failure
      if (currentSessionId.current) {
        trackOAuthAuthentication(currentSessionId.current, false, categorizedError.message);
      }
      
      // Log security event
      logOAuthFailure(cspAccountId, categorizedError.message);
      
      toast({
        title: categorizedError.userMessage,
        description: categorizedError.actionable,
        variant: "destructive",
      });
      
      setIsAuthenticating(false);
      return false;
    }
  };

  // Public interface with debouncing
  const startOAuth = async (cspAccountId: string) => {
    return await debouncedStartOAuth(cspAccountId);
  };

  // Enhanced retry mechanism with rate limiting respect
  const retryAuthentication = useCallback(async (cspAccountId: string) => {
    if (retryCount >= MAX_RETRY_ATTEMPTS) {
      logWarning('Maximum retry attempts reached', { metadata: { cspAccountId, retryCount } });
      toast({
        title: "Maximum retries reached",
        description: "Please wait a moment and try again",
        variant: "destructive",
      });
      return;
    }

    // Clear rate limit on manual retry to allow immediate retry
    clearRateLimit(cspAccountId);

    const delay = getRetryDelay(retryCount + 1);
    logger.info(`Retrying authentication in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`);
    
    setRetryCount(prev => prev + 1);
    
    // Track retry
    if (currentSessionId.current) {
      trackOAuthRetry(currentSessionId.current);
    }
    
    retryTimeoutRef.current = setTimeout(async () => {
      await startOAuthInternal(cspAccountId);
    }, delay);
  }, [retryCount]);
  
  // Fetch trading accounts with enhanced caching and network resilience
  const fetchTradingAccounts = async (accessToken: string) => {
    if (fetchAttemptedRef.current) {
      return [];
    }
    
    fetchAttemptedRef.current = true;
    setIsLoading(true);
    
    const startTime = Date.now();

    try {
      // Try memory cache first - get cspAccountId from URL or localStorage
      const urlParams = new URLSearchParams(window.location.search);
      const currentCspAccountId = window.location.pathname.split('/').pop() || 
                                  urlParams.get('cspAccountId') || 
                                  localStorage.getItem('current_csp_account_id') || '';
      
      const cached = memoryOAuthCache.getTradingAccounts(currentCspAccountId);
      if (cached && cached.length > 0) {
        logger.info('Using cached trading accounts from memory');
        trackCacheHit(true);
        const formattedAccounts = formatTradingAccounts(cached);
        setTradingAccounts(formattedAccounts);
        setRawAccounts(cached);
        
        // Track successful fetch
        if (currentSessionId.current) {
          trackAccountsFetch(currentSessionId.current, true, cached.length);
        }
        
        logPerformance('fetchTradingAccounts_cache', startTime);
        return formattedAccounts;
      }

      trackCacheHit(false);

      // Fetch with network resilience
      const accounts = await apiConnectionService.getTradingAccounts(accessToken);
      
      const validAccounts = accounts
        .map((account: any) => ({
          accountId: account.accountId?.toString() || '',
          accountNumber: account.accountNumber?.toString() || '',
          live: !!account.live,
          balance: account.balance || 0,
          depositCurrency: account.depositCurrency || 'USD',
          moneyDigits: account.moneyDigits || 2,
          brokerName: account.brokerName,
          brokerTitle: account.brokerTitle
        }))
        .filter(account => !!account.accountId);
      
      setRawAccounts(validAccounts);
      
      // Cache the results with memory-first approach
      if (currentCspAccountId) {
        memoryOAuthCache.setTradingAccounts(validAccounts, currentCspAccountId);
      }
      
      const formattedAccounts = formatTradingAccounts(validAccounts);
      setTradingAccounts(formattedAccounts);
      
      // Track successful fetch
      if (currentSessionId.current) {
        trackAccountsFetch(currentSessionId.current, true, validAccounts.length);
      }
      
      logPerformance('fetchTradingAccounts_fetch', startTime);
      
      if (formattedAccounts.length > 0) {
        logOAuthFlow('fetch_accounts', true, { metadata: { accountCount: formattedAccounts.length } });
        toast({
          title: "Accounts Retrieved",
          description: `Found ${formattedAccounts.length} trading accounts.`,
        });
      } else {
        logWarning('No trading accounts found');
        toast({
          title: "No Trading Accounts",
          description: "No trading accounts were found for this API connection.",
          variant: "destructive",
        });
      }
      
      return formattedAccounts;
    } catch (error) {
      logError('Fetch trading accounts error', error as Error);
      
      const categorizedError = categorizeOAuthError(error);
      setCurrentError(categorizedError);
      
      // Track failed fetch
      if (currentSessionId.current) {
        trackAccountsFetch(currentSessionId.current, false, 0, categorizedError.message);
      }
      
      toast({
        title: categorizedError.userMessage,
        description: categorizedError.actionable,
        variant: "destructive",
      });
      
      setTradingAccounts([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to format trading accounts
  const formatTradingAccounts = (accounts: any[]): TradingAccountOption[] => {
    return accounts.map((account) => {
      const balance = account.moneyDigits > 0 
        ? account.balance / Math.pow(10, account.moneyDigits) 
        : account.balance;
        
      return {
        id: account.accountId,
        name: account.accountNumber,
        balance: balance || 0,
        isLive: Boolean(account.live),
        currency: account.depositCurrency || 'USD',
        accountNumber: account.accountNumber
      };
    });
  };

  return {
    isAuthenticating,
    isLoading,
    tokens,
    tradingAccounts,
    rawAccounts,
    currentError,
    retryCount,
    canRetry: currentError?.canRetry && retryCount < MAX_RETRY_ATTEMPTS,
    startOAuth,
    retryAuthentication,
    resetState
  };
}

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => Promise<ReturnType<T>> {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>): Promise<ReturnType<T>> => {
    return new Promise((resolve) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => resolve(func(...args)), wait);
    });
  };
}
